import {
  App,
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Empty,
  Input,
  Modal,
  Popover,
  Radio,
  Result,
  Segmented,
  Select,
  Skeleton,
  Space,
  Spin,
  Tag,
  Tooltip,
} from 'antd';
import {
  ClockCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  LinkOutlined,
  SaveOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PageTitle from 'components/page-title';
import { extractErrorMessage } from 'pages/attendance-manager/component/export-all-modal';
import {
  HistoryItem,
  AccessInfo,
  AgentInfo,
  CreatorHit,
  GroupInfo,
  CompareMode,
  ExtractColumn,
  LookupBody,
  LookupMode,
  LookupResult,
  LookupRow,
  MonthInfo,
  SavedListMeta,
  TokenResult,
  TokenStatus,
  errorText,
  kcLookupApi,
} from './api';
import {
  SOURCE_TEXT,
  SavedList,
  daysText,
  dm,
  dmy,
  fmtHours,
  fmtNum,
  fmtPct,
  fmtShort,
  loadSaved,
  monthHint,
  monthText,
  storeSaved,
} from './format';
import ResultTable, { GroupBy } from './result-table';
import StackChart from './stack-chart';
import styles from './style.module.scss';

const TITLE = 'Tra cứu KC';

const TOKEN_LABEL: Record<TokenStatus, string> = {
  OK: 'khớp',
  RENAMED: 'đổi TikTok ID',
  DUPLICATE: 'trùng, tính 1 lần',
  NOT_FOUND: 'không tìm thấy',
  INVALID: 'không hợp lệ',
  NO_DATA: 'không có KC kỳ này',
  OTHER_EMAIL: 'thuộc Agency khác',
  OUT_OF_SCOPE: 'ngoài phạm vi',
};
const TOKEN_CLASS: Record<TokenStatus, string> = {
  OK: styles.chipOk,
  RENAMED: styles.chipRen,
  DUPLICATE: styles.chipWarn,
  NOT_FOUND: styles.chipBad,
  INVALID: styles.chipBad,
  NO_DATA: styles.chipMute,
  OTHER_EMAIL: styles.chipWarn,
  OUT_OF_SCOPE: styles.chipMute,
};

type PeriodKind = 'MONTHS' | 'RANGE';
type Quick = 'all' | 'kc' | 'up' | 'drop' | 'list' | 'email';
type RowFilter = null | 'noData' | 'renamed' | 'emailChanged' | 'departed';

function tokenTip(t: TokenResult): string {
  switch (t.status) {
    case 'RENAMED':
      return `@${t.note} là TikTok ID cũ → @${t.username} (UID ${t.uid})`;
    case 'DUPLICATE':
      return `Cùng người với "${t.note}" — chỉ tính 1 lần`;
    case 'NOT_FOUND':
      return t.suggest
        ? `Không có "${t.input}". Có phải ${t.suggest}? Bấm để sửa.`
        : 'Chưa có trong file TikTok nào đã nhập';
    case 'INVALID':
      return t.note || 'Không giống UID hay TikTok ID';
    case 'OUT_OF_SCOPE':
      return 'Creator không thuộc nhóm / Studio bạn quản lý — không hiện số';
    case 'OTHER_EMAIL':
      return `Đang thuộc ${t.note} — không phải Nhóm / email đã chọn`;
    case 'NO_DATA':
      return 'Có trong hệ thống nhưng kỳ này không có dòng số liệu';
    default:
      return `UID ${t.uid}`;
  }
}

const KcLookupPage = () => {
  const { message, notification, modal } = App.useApp();
  const location = useLocation();
  const [access, setAccess] = useState<AccessInfo | null>(null);
  const canUse = access === null ? null : access.canUse;
  const [months, setMonths] = useState<MonthInfo[]>([]);
  const [today, setToday] = useState(dayjs().format('YYYY-MM-DD'));

  // ---- điều kiện
  const [idsText, setIdsText] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  // Nhóm / Agency theo cột D file TikTok.
  const [groups, setGroups] = useState<string[]>([]);
  const [mode, setMode] = useState<LookupMode>('UNION');
  const [periodKind, setPeriodKind] = useState<PeriodKind>('MONTHS');
  const [selMonths, setSelMonths] = useState<string[]>([]);
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [compare, setCompare] = useState<CompareMode>('PREV');
  const [groupBy, setGroupBy] = useState<GroupBy>('email');
  const [quick, setQuick] = useState<Quick>('all');
  const [rowFilter, setRowFilter] = useState<RowFilter>(null);

  // ---- dữ liệu
  const [agents, setAgents] = useState<AgentInfo[]>([]);
  const [groupList, setGroupList] = useState<GroupInfo[]>([]);
  const [hits, setHits] = useState<CreatorHit[]>([]);
  const [searching, setSearching] = useState(false);
  const searchSeq = useRef(0);
  const [result, setResult] = useState<LookupResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  // Bộ ID đã lưu trên máy chủ (đợt 2); `legacy` = bộ lưu trong trình duyệt ở đợt 1, chờ chuyển lên.
  const [lists, setLists] = useState<SavedListMeta[]>([]);
  const [active, setActive] = useState<{ meta: SavedListMeta; snapshot: string } | null>(null);
  const [legacy, setLegacy] = useState<SavedList[]>(() => loadSaved());
  const [saveName, setSaveName] = useState('');
  const [saveShared, setSaveShared] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  // Lịch sử tra cứu của chính mình (đợt 3) — tải khi mở khay.
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [extract, setExtract] = useState<{ name: string; columns: ExtractColumn[]; pick: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const seq = useRef(0);

  useEffect(() => {
    kcLookupApi
      .access()
      .then((a) => setAccess(a || { canUse: false, scope: null, groups: [] }))
      .catch(() => setAccess({ canUse: false, scope: null, groups: [] }));
  }, []);

  // Link chia sẻ: ?list=ID&months=2026-08,2026-09 | &from=&to= · &compare=
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  useEffect(() => {
    if (!canUse) return;
    kcLookupApi
      .months()
      .then((r) => {
        setMonths(r.months || []);
        setToday(r.today);
        const known = new Set((r.months || []).map((m) => m.month));
        const fromUrl = (urlParams.get('months') || '').split(',').filter((m) => /^\d{4}-\d{2}$/.test(m));
        const from = urlParams.get('from');
        const to = urlParams.get('to');
        const cmp = urlParams.get('compare');
        if (cmp === 'NONE' || cmp === 'PREV' || cmp === 'SAME_DAYS') setCompare(cmp);
        if (from && to && dayjs(from).isValid() && dayjs(to).isValid()) {
          setPeriodKind('RANGE');
          setRange([dayjs(from), dayjs(to)]);
        } else if (fromUrl.length) {
          setSelMonths(fromUrl.filter((m) => known.has(m) || m <= r.today.slice(0, 7)));
        } else {
          // Mặc định: tháng gần nhất đã có file tháng, không thì tháng đang chạy.
          const done = (r.months || []).find((m) => m.status === 'MONTH_FILE' || m.status === 'DAILY_COMPLETE');
          setSelMonths([done?.month || r.months?.[0]?.month].filter(Boolean) as string[]);
        }
      })
      .catch((e) => setError(errorText(e)));
    reloadLists();
    const listId = Number(urlParams.get('list'));
    if (listId > 0) openList(listId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUse]);

  const period = useMemo(() => {
    if (periodKind === 'RANGE')
      return range ? { from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') } : null;
    return selMonths.length ? { months: [...selMonths].sort() } : null;
  }, [periodKind, range, selMonths]);
  const periodKey = JSON.stringify(period);

  useEffect(() => {
    if (!canUse || !period) return;
    kcLookupApi
      .agents(period)
      .then((r) => setAgents(r.agents || []))
      .catch(() => setAgents([]));
    kcLookupApi
      .groups(period)
      .then((r) => setGroupList(r.groups || []))
      .catch(() => setGroupList([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUse, periodKey]);

  const body: LookupBody | null = useMemo(() => {
    if (!period) return null;
    const ids = idsText.trim() ? [idsText] : [];
    if (!ids.length && !emails.length && !groups.length) return null;
    return { ids, emails, groups, mode, compare, ...period };
  }, [period, idsText, emails, groups, mode, compare]);
  const bodyKey = JSON.stringify(body);

  // Tự tra sau khi ngừng gõ / chọn 500 ms.
  useEffect(() => {
    if (!canUse) return;
    if (!body) {
      setResult(null);
      setError(null);
      return;
    }
    const id = ++seq.current;
    const t = window.setTimeout(() => {
      setLoading(true);
      kcLookupApi
        .query(body)
        .then((r) => {
          if (id !== seq.current) return;
          setResult(r);
          setError(null);
        })
        .catch((e) => id === seq.current && setError(errorText(e)))
        .finally(() => id === seq.current && setLoading(false));
    }, 500);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUse, bodyKey]);

  // ---- lọc bảng
  const rows: LookupRow[] = useMemo(() => {
    let list = result?.rows || [];
    if (rowFilter === 'noData') list = list.filter((r) => r.diamonds === 0);
    if (rowFilter === 'renamed') list = list.filter((r) => r.renamedFrom);
    if (rowFilter === 'emailChanged') list = list.filter((r) => r.emailChanged);
    if (rowFilter === 'departed') list = list.filter((r) => r.departedAt);
    if (quick === 'kc') list = list.filter((r) => r.diamonds > 0);
    if (quick === 'up') list = list.filter((r) => r.deltaPct !== null && r.deltaPct > 0);
    if (quick === 'drop') list = list.filter((r) => r.deltaPct !== null && r.deltaPct < -0.3);
    if (quick === 'list') list = list.filter((r) => r.fromList);
    if (quick === 'email') list = list.filter((r) => !r.fromList);
    return list;
  }, [result, quick, rowFilter]);

  // ---- file
  const onFile = async (file: File) => {
    if (/\.txt$/i.test(file.name)) {
      const text = await file.text();
      setIdsText((s) => (s.trim() ? `${s}\n${text}` : text));
      message.success(`Đã thêm từ ${file.name}`);
      return;
    }
    try {
      const r = await kcLookupApi.extract(file);
      if (!r.columns?.length) return message.warning('File không có cột nào có chữ');
      setExtract({ name: file.name, columns: r.columns, pick: r.columns[0].index });
    } catch (e) {
      message.error(errorText(e));
    }
  };
  const applyExtract = () => {
    if (!extract) return;
    const col = extract.columns.find((c) => c.index === extract.pick);
    if (col) {
      const text = col.values.join('\n');
      setIdsText((s) => (s.trim() ? `${s}\n${text}` : text));
      message.success(`Đã thêm ${col.values.length} mục từ cột "${col.header}"`);
    }
    setExtract(null);
  };

  // ---- bộ ID đã lưu (máy chủ)
  const snapshotOf = (ids: string, em: string[], md: LookupMode, gr: string[] = []) =>
    JSON.stringify([ids.trim(), [...em].sort(), md, [...gr].sort()]);
  const dirty = !!active && active.snapshot !== snapshotOf(idsText, emails, mode, groups);

  function reloadLists() {
    kcLookupApi
      .lists()
      .then((r) => setLists(r.lists || []))
      .catch(() => setLists([]));
  }

  async function openList(id: number) {
    try {
      const l = await kcLookupApi.getList(id);
      setIdsText(l.ids || '');
      setEmails(l.emails || []);
      setGroups(l.groups || []);
      setMode(l.mode || 'UNION');
      setActive({ meta: l, snapshot: snapshotOf(l.ids || '', l.emails || [], l.mode || 'UNION', l.groups || []) });
    } catch (e) {
      message.error(errorText(e));
    }
  }

  // Sửa TikTok ID gõ sai theo gợi ý: thay đúng mục đó trong ô danh sách, giữ nguyên dấu phân cách.
  const fixToken = (input: string, suggestion: string) =>
    setIdsText((s) =>
      s
        .split(/([\s,;|]+)/)
        .map((part) => (part.replace(/^@/, '').toLowerCase() === input.replace(/^@/, '').toLowerCase() ? suggestion : part))
        .join('')
    );
  const fixAll = () => {
    const fixes = (result?.tokens || []).filter((t) => t.status === 'NOT_FOUND' && t.suggest);
    fixes.forEach((t) => fixToken(t.input, t.suggest as string));
    if (fixes.length) message.success(`Đã sửa ${fixes.length} ID theo gợi ý`);
  };

  const loadHistory = () =>
    kcLookupApi
      .history()
      .then((r) => setHistory(r.items || []))
      .catch(() => setHistory([]));
  const applyHistory = (h: HistoryItem) => {
    const b = h.body;
    if (!b) return;
    setIdsText(b.ids || '');
    setEmails(b.emails || []);
    setGroups(b.groups || []);
    setMode(b.mode || 'UNION');
    setCompare(b.compare || 'PREV');
    if (b.from && b.to) {
      setPeriodKind('RANGE');
      setRange([dayjs(b.from), dayjs(b.to)]);
    } else if (b.months?.length) {
      setPeriodKind('MONTHS');
      setSelMonths(b.months);
    }
    setActive(null);
    setHistoryOpen(false);
  };
  const clearHistory = async () => {
    try {
      await kcLookupApi.removeHistory();
      setHistory([]);
    } catch (e) {
      message.error(errorText(e));
    }
  };

  const saveCurrent = async (asNew: boolean) => {
    const name = saveName.trim();
    if (!name) return message.warning('Đặt tên cho bộ ID');
    const payload = { name, ids: idsText, emails, groups, mode, shared: saveShared };
    try {
      const l =
        !asNew && active?.meta.canEdit
          ? await kcLookupApi.updateList(active.meta.id, payload)
          : await kcLookupApi.createList(payload);
      setActive({ meta: l, snapshot: snapshotOf(l.ids, l.emails, l.mode, l.groups) });
      setSaveOpen(false);
      reloadLists();
      message.success(`Đã lưu "${l.name}"${l.shared ? ' · chia sẻ cho mọi người dùng trang' : ''}`);
    } catch (e) {
      message.error(errorText(e));
    }
  };

  const removeList = (l: SavedListMeta) =>
    modal.confirm({
      title: `Xoá bộ "${l.name}"?`,
      content: l.shared ? 'Bộ đang chia sẻ: người khác sẽ không mở được nữa, kể cả qua link.' : undefined,
      okText: 'Xoá',
      okButtonProps: { danger: true },
      cancelText: 'Huỷ',
      onOk: async () => {
        try {
          await kcLookupApi.removeList(l.id);
          if (active?.meta.id === l.id) setActive(null);
          reloadLists();
        } catch (e) {
          message.error(errorText(e));
        }
      },
    });

  const migrateLegacy = async () => {
    let ok = 0;
    for (const l of legacy) {
      try {
        await kcLookupApi.createList({ name: l.name.slice(0, 80), ids: l.ids, emails: l.emails || [], mode: l.mode });
        ok += 1;
      } catch {
        /* bộ lỗi giữ lại trong trình duyệt */
      }
    }
    if (ok === legacy.length) {
      storeSaved([]);
      setLegacy([]);
    }
    reloadLists();
    message.success(`Đã chuyển ${ok}/${legacy.length} bộ lên máy chủ`);
  };

  // Link chỉ chứa mã bộ + kỳ (không đưa ID / email lên URL).
  const copyLink = () => {
    if (!active || dirty) return message.warning('Lưu bộ ID trước (hoặc lưu lại thay đổi) để có link chia sẻ');
    const q = new URLSearchParams({ list: String(active.meta.id) });
    if (periodKind === 'RANGE' && range) {
      q.set('from', range[0].format('YYYY-MM-DD'));
      q.set('to', range[1].format('YYYY-MM-DD'));
    } else if (selMonths.length) q.set('months', [...selMonths].sort().join(','));
    if (compare !== 'PREV') q.set('compare', compare);
    const link = `${window.location.origin}${location.pathname}?${q.toString()}`;
    const copy = () =>
      navigator.clipboard?.writeText(link).then(
        () => message.success('Đã sao chép link'),
        () => message.info(link)
      );
    if (!active.meta.shared && active.meta.canEdit)
      return modal.confirm({
        title: 'Bộ ID đang riêng tư',
        content: 'Người khác mở link sẽ không thấy bộ này. Bật chia sẻ cho mọi người dùng trang?',
        okText: 'Bật chia sẻ và sao chép',
        cancelText: 'Chỉ sao chép',
        onOk: async () => {
          const l = await kcLookupApi.updateList(active.meta.id, { shared: true });
          setActive({ meta: l, snapshot: active.snapshot });
          reloadLists();
          copy();
        },
        onCancel: copy,
      });
    copy();
  };

  // Thêm ID vào ô danh sách, mỗi ID một dòng, bỏ "@" ở đầu (bonitagirl.hp = @bonitagirl.hp).
  const addTokens = (raw: string) => {
    const list = raw
      .split(/[\s,;|]+/)
      .map((t) => t.trim().replace(/^@/, ''))
      .filter(Boolean);
    if (!list.length) return 0;
    setIdsText((s) => (s.trim() ? `${s.replace(/\s+$/, '')}\n${list.join('\n')}` : list.join('\n')));
    return list.length;
  };

  // Ô tìm từng người (cột C / B): gõ ≥ 2 ký tự, chờ 300 ms. Dán / gõ NHIỀU ID (cách nhau dấu cách, phẩy…)
  // → thêm thẳng tất cả vào danh sách.
  const [searchText, setSearchText] = useState('');
  const searchTimer = useRef<number | undefined>(undefined);
  const onSearch = (q: string) => {
    window.clearTimeout(searchTimer.current);
    // Dấu cách / phẩy / xuống dòng "chốt" các ID đứng trước nó; phần cuối (đang gõ dở) ở lại để tìm tiếp.
    const parts = q.split(/[\s,;|]+/);
    const tail = parts.pop() || '';
    const done = parts.filter(Boolean);
    if (done.length) {
      const n = addTokens(done.join(' '));
      if (n > 1) message.success(`Đã thêm ${n} ID`);
    }
    setSearchText(done.length ? tail : q);
    const text = (done.length ? tail : q).trim();
    if (text.replace(/^@/, '').length < 2 || !period) {
      setHits([]);
      return;
    }
    searchTimer.current = window.setTimeout(() => {
      const id = ++searchSeq.current;
      setSearching(true);
      kcLookupApi
        .search(text, period)
        .then((r) => id === searchSeq.current && setHits(r.creators || []))
        .catch(() => id === searchSeq.current && setHits([]))
        .finally(() => id === searchSeq.current && setSearching(false));
    }, 300);
  };

  const copySummary = useCallback(() => {
    if (!result) return;
    const span = result.segments
      .map((s) => (s.full ? monthText(s.key) : `${dm(s.start)}→${dm(s.end)}/${s.key.slice(0, 4)}`))
      .join(' + ');
    const text = `${result.totals.creators} Creator · ${span} · ${fmtNum(result.totals.diamonds)} KC`;
    navigator.clipboard?.writeText(text).then(
      () => message.success(`Đã sao chép: ${text}`),
      () => message.info(text)
    );
  }, [result, message]);

  const doExport = async () => {
    if (!body) return;
    setExporting(true);
    try {
      await kcLookupApi.exportXlsx(body);
    } catch (e) {
      notification.error({ message: 'Không xuất được file', description: await extractErrorMessage(e) });
    } finally {
      setExporting(false);
    }
  };

  // ---------------------------------------------------------------- render

  if (canUse === null) return <Skeleton active />;
  if (!canUse)
    return (
      <Result
        status="info"
        title="Chưa dùng được Tra cứu KC"
        subTitle="Trang dành cho Admin, MOD được Admin cho phép Nhập dữ liệu (Doanh thu → Nhập dữ liệu → Quyền), Quản lý Studio và Agency có nhóm."
      />
    );

  const toggleMonth = (m: string) =>
    setSelMonths((cur) => (cur.includes(m) ? (cur.length > 1 ? cur.filter((x) => x !== m) : cur) : [...cur, m]));
  const presetMonths = (n: number, skipRunning: boolean) => {
    const list = months.filter((m) => m.status !== 'FUTURE' && !(skipRunning && m.status === 'RUNNING'));
    setSelMonths(list.slice(0, n).map((m) => m.month));
  };

  // Cũ → mới; bỏ các tháng cũ nhất chưa có dữ liệu.
  const firstWithData = [...months].reverse().findIndex((m) => m.status !== 'NO_DATA');
  const shownMonths = [...months].reverse().slice(Math.max(0, firstWithData));
  const tokens = result?.tokens || [];
  const counts = result?.tokenCounts || {};
  const multi = (result?.segments.length || 0) > 1;
  const review = result?.review;
  const missingSeg = result?.segments.filter((s) => s.missingDays.length) || [];
  const unsplitSeg = result?.segments.filter((s) => s.unsplittableDays.length) || [];
  const tokensOf = (...st: TokenStatus[]) => tokens.filter((x) => st.includes(x.status));

  const reviewCards: {
    key: string;
    label: string;
    hint: string;
    count: number;
    tone: 'bad' | 'warn' | 'info' | 'mute';
    filter?: RowFilter;
    list?: string[];
    extra?: JSX.Element;
  }[] = review
    ? [
        ...(review.outOfScope
          ? [
              {
                key: 'oos',
                label: 'Ngoài phạm vi',
                hint: 'không thuộc nhóm bạn quản lý',
                count: review.outOfScope,
                tone: 'mute' as const,
                list: tokensOf('OUT_OF_SCOPE').map((x) => x.input),
              },
            ]
          : []),
        { key: 'nf', label: 'Không tìm thấy', hint: 'chưa có trong file nào', count: review.notFound + review.invalid, tone: 'bad', list: tokensOf('NOT_FOUND', 'INVALID').map((x) => `${x.input}${x.suggest ? ` — có phải ${x.suggest}?` : x.note ? ` — ${x.note}` : ''}`) },
        { key: 'dup', label: 'Trùng', hint: 'cùng một người, đã tính 1 lần', count: review.duplicate, tone: 'warn', list: tokensOf('DUPLICATE').map((x) => `${x.input} = ${x.note}`) },
        { key: 'oth', label: 'Thuộc email khác', hint: 'chế độ Giao ∩, không vào tổng', count: review.otherEmail, tone: 'warn', list: tokensOf('OTHER_EMAIL').map((x) => `@${x.username || x.input} → ${x.note}`) },
        { key: 'nd', label: 'Không có KC kỳ này', hint: '0 KC trong kỳ đã chọn', count: (result?.rows || []).filter((r) => r.diamonds === 0).length, tone: 'mute', filter: 'noData' },
        { key: 'ren', label: 'Đổi TikTok ID', hint: 'đã tự quy về UID', count: review.renamed, tone: 'info', filter: 'renamed' },
        { key: 'chg', label: 'Đổi Đại diện trong kỳ', hint: 'có số dưới nhiều email', count: review.emailChanged, tone: 'warn', filter: 'emailChanged' },
        { key: 'dep', label: 'Đã rời đại lý', hint: 'không còn số sau ngày rời', count: review.departed, tone: 'bad', filter: 'departed' },
        {
          key: 'miss',
          label: 'Ngày chưa nhập',
          hint: 'tổng chưa đủ',
          count: review.missingDays,
          tone: 'warn',
          list: missingSeg.map((s) => `${monthText(s.key)}: ${daysText(s.missingDays, 10)}`),
          extra: missingSeg.length ? (
            <Link to={`/data-import?tab=ngay&month=${missingSeg[0].key}`}>Mở Nhập dữ liệu</Link>
          ) : undefined,
        },
      ]
    : [];

  return (
    <div className={styles.page}>
      <PageTitle
        title={TITLE}
        description="Chọn theo Nhóm / Agency (cột D), email Đại diện (cột E) và / hoặc từng TikTok ID (cột C) → bảng tổng hợp + Tổng Kim cương. Mỗi tháng chỉ lấy một nguồn số liệu: file tháng nếu có, không thì cộng file ngày."
      />

      {access?.scope === 'GROUPS' && (
        <Alert
          type="info"
          showIcon
          message={`Bạn xem trong phạm vi: ${access.groups.join(', ') || 'nhóm của bạn'}`}
          description="Chỉ Creator thuộc nhóm / Studio bạn quản lý mới có số; ID khác hiện “ngoài phạm vi”. Phạm vi lấy theo Phân tích của từng tháng."
        />
      )}

      <section className={styles.bar}>
        {/* 1. Ai? */}
        <div className={styles.box}>
          <div className={styles.boxHead}>
            <span className={styles.step}>1</span>
            <b>Ai?</b>
            <span className={styles.hint}>UID, TikTok ID (có hay không có @ đều được), link — mỗi dòng hoặc cách nhau dấu cách / dấu phẩy</span>
          </div>
          <Select
            id="kc-search"
            showSearch
            value={null}
            searchValue={searchText}
            placeholder="Gõ TikTok ID (cột C) hoặc UID — không cần @; dán nhiều ID cũng được"
            onInputKeyDown={(e) => {
              // Không có gợi ý nào (vd ID chưa có trong kỳ) mà vẫn Enter → thêm nguyên chữ đã gõ.
              if (e.key === 'Enter' && !hits.length && searchText.trim()) {
                e.preventDefault();
                addTokens(searchText);
                setSearchText('');
              }
            }}
            filterOption={false}
            notFoundContent={searching ? <Spin size="small" /> : 'Gõ ít nhất 2 ký tự'}
            onSearch={onSearch}
            onChange={(uid) => {
              const h = hits.find((x) => x.uid === uid);
              if (!h) return;
              addTokens(h.username || h.uid);
              setSearchText('');
              setHits([]);
            }}
            options={hits.map((h) => ({
              value: h.uid,
              label: (
                <div className={styles.agentOpt}>
                  <span>
                    <b>{h.username ? `@${h.username}` : h.uid}</b>
                    <small>
                      {h.groupName || '—'} · {h.uid}
                    </small>
                  </span>
                  <span className={styles.agentNum}>{fmtShort(h.diamonds)}</span>
                </div>
              ),
            }))}
            style={{ width: '100%' }}
          />
          <Input.TextArea
            id="kc-ids"
            value={idsText}
            onChange={(e) => setIdsText(e.target.value)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              const f = e.dataTransfer.files?.[0];
              if (f) {
                e.preventDefault();
                onFile(f);
              }
            }}
            autoSize={{ minRows: 4, maxRows: 9 }}
            placeholder={'bonitagirl.hp\n_nhi0611_ yanlinh1370\n7359135153148395525\n… hoặc thả file Excel / CSV vào đây'}
            className={styles.ids}
            spellCheck={false}
          />
          <div className={styles.row}>
            <Button size="small" icon={<FileExcelOutlined />} onClick={() => fileRef.current?.click()}>
              Lấy từ file Excel / CSV
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.csv,.txt"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onFile(f);
                e.target.value = '';
              }}
            />
            <Select
              size="small"
              placeholder="Bộ ID đã lưu"
              value={active?.meta.id ?? null}
              style={{ minWidth: 200, maxWidth: 280 }}
              showSearch
              optionFilterProp="search"
              onChange={(v) => v && openList(v)}
              options={lists.map((l) => ({
                value: l.id,
                search: `${l.name} ${l.ownerName || ''}`,
                label: (
                  <span className={styles.savedOpt}>
                    <span className={styles.savedName}>
                      {l.shared && <TeamOutlined title="Chia sẻ" />} {l.name}
                      <small>
                        {l.count} ID{l.groups?.length ? ` · ${l.groups.length} nhóm` : ''}
                        {l.emails.length ? ` · ${l.emails.length} email` : ''}
                        {l.mine ? '' : ` · ${l.ownerName || 'người khác'}`}
                      </small>
                    </span>
                    {l.canEdit && (
                      <DeleteOutlined
                        onClick={(e) => {
                          e.stopPropagation();
                          removeList(l);
                        }}
                      />
                    )}
                  </span>
                ),
              }))}
              notFoundContent="Chưa có bộ nào"
              popupRender={(menu) => (
                <>
                  {menu}
                  {legacy.length > 0 && (
                    <div className={styles.legacy}>
                      {legacy.length} bộ cũ lưu trong trình duyệt này
                      <Button size="small" type="link" onClick={migrateLegacy}>
                        Chuyển lên máy chủ
                      </Button>
                    </div>
                  )}
                </>
              )}
            />
            <Popover
              trigger="click"
              open={saveOpen}
              onOpenChange={(o) => {
                setSaveOpen(o);
                if (o) {
                  setSaveName(active?.meta.name || '');
                  setSaveShared(!!active?.meta.shared);
                }
              }}
              content={
                <div className={styles.saveBox}>
                  <Input
                    id="kc-save-name"
                    size="small"
                    placeholder="Tên bộ, vd Team Royal"
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    onPressEnter={() => saveCurrent(!active?.meta.canEdit)}
                    maxLength={80}
                  />
                  <Checkbox id="kc-save-shared" checked={saveShared} onChange={(e) => setSaveShared(e.target.checked)}>
                    Chia sẻ cho mọi người dùng trang
                  </Checkbox>
                  <Space wrap>
                    {active?.meta.canEdit && (
                      <Button size="small" type="primary" onClick={() => saveCurrent(false)}>
                        Cập nhật bộ đang mở
                      </Button>
                    )}
                    <Button size="small" type={active?.meta.canEdit ? 'default' : 'primary'} onClick={() => saveCurrent(true)}>
                      Lưu thành bộ mới
                    </Button>
                  </Space>
                </div>
              }
            >
              <Button size="small" icon={<SaveOutlined />} disabled={!idsText.trim() && !emails.length && !groups.length}>
                {active && dirty ? 'Lưu thay đổi' : 'Lưu bộ'}
              </Button>
            </Popover>
            <Popover
              trigger="click"
              placement="bottomLeft"
              open={historyOpen}
              onOpenChange={(o) => {
                setHistoryOpen(o);
                if (o) loadHistory();
              }}
              title="Tra cứu gần đây của bạn"
              content={
                <div className={styles.history}>
                  {history.length ? (
                    history.map((h) => (
                      <button type="button" key={h.id} className={styles.historyItem} onClick={() => applyHistory(h)}>
                        <b>{h.label}</b>
                        <span>
                          {fmtNum(h.totalKc)} KC · {h.creators} Creator · {dayjs(h.updatedAt).format('HH:mm DD/MM')}
                        </span>
                      </button>
                    ))
                  ) : (
                    <span className={styles.hint}>Chưa có lần tra nào.</span>
                  )}
                  {history.length > 0 && (
                    <Button size="small" type="link" danger onClick={clearHistory}>
                      Xoá lịch sử
                    </Button>
                  )}
                </div>
              }
            >
              <Button size="small" icon={<ClockCircleOutlined />}>
                Gần đây
              </Button>
            </Popover>
            <Tooltip title={active ? (dirty ? 'Có thay đổi chưa lưu' : 'Link mở đúng bộ ID + kỳ đang xem') : 'Lưu bộ ID trước'}>
              <Button size="small" icon={<LinkOutlined />} disabled={!active} onClick={copyLink}>
                Sao chép link
              </Button>
            </Tooltip>
            {idsText && (
              <Button
                size="small"
                type="text"
                onClick={() => {
                  setIdsText('');
                  setActive(null);
                }}
              >
                Xoá
              </Button>
            )}
          </div>
          {tokens.length > 0 && (
            <>
              <div className={styles.counts}>
                <span className={styles.pill}>{tokens.length} mục</span>
                {tokens.some((x) => x.status === 'NOT_FOUND' && x.suggest) && (
                  <button type="button" className={`${styles.pill} ${styles.chipFix}`} onClick={fixAll}>
                    Sửa {tokens.filter((x) => x.status === 'NOT_FOUND' && x.suggest).length} ID theo gợi ý
                  </button>
                )}
                {(Object.keys(TOKEN_LABEL) as TokenStatus[])
                  .filter((k) => counts[k])
                  .map((k) => (
                    <span key={k} className={`${styles.pill} ${TOKEN_CLASS[k]}`}>
                      {counts[k]} {TOKEN_LABEL[k]}
                    </span>
                  ))}
              </div>
              <div className={styles.chips}>
                {tokens.slice(0, 400).map((x, i) => (
                  <Tooltip key={`${i}-${x.input}`} title={tokenTip(x)}>
                    {x.status === 'NOT_FOUND' && x.suggest ? (
                      <button
                        type="button"
                        className={`${styles.chip} ${styles.chipFix}`}
                        onClick={() => fixToken(x.input, x.suggest as string)}
                      >
                        {x.input} → {x.suggest}?
                      </button>
                    ) : (
                      <span className={`${styles.chip} ${TOKEN_CLASS[x.status]}`}>
                        {x.status === 'RENAMED'
                          ? `↻ ${x.note} → ${x.username}`
                          : x.status === 'DUPLICATE'
                          ? `⧉ ${x.input}`
                          : x.username || x.input}
                      </span>
                    )}
                  </Tooltip>
                ))}
                {tokens.length > 400 && <span className={styles.hint}>… và {tokens.length - 400} mục nữa</span>}
              </div>
            </>
          )}
        </div>

        {/* 2. Agency */}
        <div className={styles.box}>
          <div className={styles.boxHead}>
            <span className={styles.step}>2</span>
            <b>Thuộc Agency nào?</b>
            <span className={styles.hint}>theo Nhóm (cột D) và / hoặc email Đại diện (cột E)</span>
          </div>
          <Segmented
            size="small"
            value={mode}
            onChange={(v) => setMode(v as LookupMode)}
            options={[
              { value: 'UNION', label: 'Hợp ∪ thêm cả nhóm' },
              { value: 'INTERSECT', label: 'Giao ∩ kiểm tra ai thuộc' },
            ]}
          />
          <Select
            id="kc-groups"
            mode="multiple"
            allowClear
            value={groups}
            onChange={setGroups}
            placeholder="Nhóm / Agency (cột D) — vd HT Agency | 12/2021"
            maxTagCount="responsive"
            optionLabelProp="value"
            filterOption={(input, opt) =>
              `${opt?.value ?? ''} ${(opt as any)?.emails ?? ''}`.toLowerCase().includes(input.toLowerCase())
            }
            options={groupList.map((g) => ({
              value: g.name,
              emails: g.emails,
              label: (
                <div className={styles.agentOpt}>
                  <span>
                    <b>{g.name}</b>
                    <small>{g.emails || '—'}</small>
                  </span>
                  <span className={styles.agentNum}>
                    {g.creators} người
                    <br />
                    {fmtShort(g.diamonds)}
                  </span>
                </div>
              ),
            }))}
            style={{ width: '100%' }}
          />
          <Select
            id="kc-emails"
            mode="multiple"
            allowClear
            value={emails}
            onChange={setEmails}
            placeholder="Email Đại diện (cột E)"
            maxTagCount="responsive"
            optionLabelProp="value"
            filterOption={(input, opt) =>
              `${opt?.value ?? ''} ${(opt as any)?.groups ?? ''}`.toLowerCase().includes(input.toLowerCase())
            }
            options={agents.map((a) => ({
              value: a.email,
              groups: a.groups,
              label: (
                <div className={styles.agentOpt}>
                  <span>
                    <b>{a.email}</b>
                    <small>{a.groups || '—'}</small>
                  </span>
                  <span className={styles.agentNum}>
                    {a.creators} người
                    <br />
                    {fmtShort(a.diamonds)}
                  </span>
                </div>
              ),
            }))}
            style={{ width: '100%' }}
          />
          <p className={styles.hint}>
            {mode === 'UNION'
              ? 'Lấy mọi Creator thuộc Nhóm HOẶC email đã chọn (chỉ phần số dưới Nhóm / email đó) + các ID ở ô 1.'
              : emails.length || groups.length
              ? 'Chỉ giữ ID ở ô 1 thuộc Nhóm / email đã chọn; ai thuộc Agency khác hiện ở “Cần xem lại”.'
              : 'Chọn Nhóm hoặc email để kiểm tra danh sách ở ô 1 có thật thuộc Agency đó không.'}
          </p>
        </div>

        {/* 3. Khi nào? */}
        <div className={styles.box}>
          <div className={styles.boxHead}>
            <span className={styles.step}>3</span>
            <b>Khi nào?</b>
            <Segmented
              size="small"
              value={periodKind}
              onChange={(v) => setPeriodKind(v as PeriodKind)}
              options={[
                { value: 'MONTHS', label: 'Theo tháng' },
                { value: 'RANGE', label: 'Khoảng ngày' },
              ]}
            />
          </div>
          {periodKind === 'MONTHS' ? (
            <>
              <div className={styles.months}>
                {shownMonths.map((m) => {
                  const h = monthHint(m);
                  const on = selMonths.includes(m.month);
                  return (
                    <button
                      type="button"
                      key={m.month}
                      className={`${styles.mo} ${on ? styles.moOn : ''}`}
                      aria-pressed={on}
                      onClick={() => toggleMonth(m.month)}
                      title={m.missingDays.length ? `Thiếu: ${daysText(m.missingDays, 12)}` : undefined}
                    >
                      <b>{monthText(m.month).replace(/\/\d{4}$/, '')}</b>
                      <small className={h.warn ? styles.warnText : ''}>{h.text}</small>
                      <i>{m.month.slice(0, 4)}</i>
                    </button>
                  );
                })}
              </div>
              <div className={styles.row}>
                <Button size="small" onClick={() => setSelMonths(months[0] ? [months[0].month] : [])}>
                  Tháng này
                </Button>
                <Button size="small" onClick={() => setSelMonths(months[1] ? [months[1].month] : [])}>
                  Tháng trước
                </Button>
                <Button size="small" onClick={() => presetMonths(3, true)}>
                  3 tháng đã xong
                </Button>
                <Button size="small" onClick={() => presetMonths(6, true)}>
                  6 tháng
                </Button>
              </div>
            </>
          ) : (
            <DatePicker.RangePicker
              id="kc-range"
              value={range}
              format="DD/MM/YYYY"
              onChange={(v) => setRange(v && v[0] && v[1] ? [v[0], v[1]] : null)}
              disabledDate={(d) => !d || d.format('YYYY-MM-DD') >= today}
              presets={[
                { label: 'Hôm qua', value: [dayjs(today).subtract(1, 'day'), dayjs(today).subtract(1, 'day')] },
                { label: '7 ngày qua', value: [dayjs(today).subtract(7, 'day'), dayjs(today).subtract(1, 'day')] },
                { label: 'Từ đầu tháng', value: [dayjs(today).startOf('month'), dayjs(today).subtract(1, 'day')] },
              ]}
              style={{ width: '100%' }}
            />
          )}
          <div className={styles.row}>
            <span className={styles.hint}>So với</span>
            <Select
              size="small"
              value={compare}
              onChange={setCompare}
              style={{ width: 210 }}
              options={[
                { value: 'PREV', label: 'Tháng trước' },
                { value: 'SAME_DAYS', label: 'Cùng số ngày tháng trước' },
                { value: 'NONE', label: 'Không so' },
              ]}
            />
            <span className={styles.hint}>Gộp theo</span>
            <Select
              size="small"
              value={groupBy}
              onChange={setGroupBy}
              style={{ width: 150 }}
              options={[
                { value: 'email', label: 'Email Đại diện' },
                { value: 'group', label: 'Nhóm TikTok' },
                { value: 'none', label: 'Không gộp' },
              ]}
            />
          </div>
          {multi && <p className={styles.hint}>Nhiều tháng → bảng hiện từng tháng một cột, cột Tổng và xu hướng; không so kỳ trước.</p>}
        </div>
      </section>

      {error && <p className={styles.error}>{error}</p>}

      {!body ? (
        <Empty
          className={styles.empty}
          description="Dán / tìm TikTok ID ở ô 1, hoặc chọn Nhóm / email Đại diện ở ô 2 để bắt đầu."
        />
      ) : !result ? (
        <Spin spinning>
          <div style={{ height: 160 }} />
        </Spin>
      ) : (
        <Spin spinning={loading}>
          <section className={styles.hero} aria-live="polite">
            <div className={styles.heroMain}>
              <div className={styles.heroLbl}>
                Tổng Kim cương ·{' '}
                {result.segments
                  .map((s) => (s.full ? monthText(s.key) : `${dmy(s.start)} → ${dmy(s.end)}`))
                  .join(' + ')}
              </div>
              <div className={styles.total}>
                <svg viewBox="0 0 24 24" aria-hidden className={styles.gem}>
                  <path d="M6 3h12l4 6-10 12L2 9z" />
                </svg>
                {fmtNum(result.totals.diamonds)}
              </div>
              {result.compare && (
                <div className={styles.cmp}>
                  {result.compare.available && result.totals.deltaPct !== null ? (
                    <span className={result.totals.deltaPct >= 0 ? styles.heroUp : styles.heroDown}>
                      {result.totals.deltaPct >= 0 ? '▲' : '▼'} {fmtPct(Math.abs(result.totals.deltaPct))}
                    </span>
                  ) : null}
                  <span>
                    {result.compare.available
                      ? `so ${dmy(result.compare.start)} → ${dmy(result.compare.end)} (${fmtNum(result.totals.compareDiamonds)})`
                      : result.compare.note}
                  </span>
                </div>
              )}
              <div className={styles.src}>
                {result.segments.map((s) => (
                  <span key={s.key}>
                    {monthText(s.key)}: {SOURCE_TEXT[s.source]}
                    {s.source === 'DAILY' || s.source === 'MIXED' ? ` (${s.coveredDays} ngày)` : ''}
                  </span>
                ))}
                {missingSeg.map((s) => (
                  <span key={`m${s.key}`} className={styles.srcWarn}>
                    ⚠ {monthText(s.key)} thiếu {s.missingDays.length} ngày: {daysText(s.missingDays, 3)} — tổng chưa đủ
                  </span>
                ))}
                {unsplitSeg.map((s) => (
                  <span key={`u${s.key}`} className={styles.srcWarn}>
                    ⚠ {monthText(s.key)}: {s.unsplittableDays.length} ngày chỉ có trong file tháng, không tách theo ngày được —
                    chọn trọn tháng
                  </span>
                ))}
              </div>
            </div>
            <div className={styles.kpis}>
              <div>
                <b>
                  {fmtNum(result.totals.creatorsWithKc)} / {fmtNum(result.totals.creators)}
                </b>
                <span>Creator có KC / trong tập</span>
              </div>
              <div>
                <b>{fmtNum(result.totals.avgPerCreator)}</b>
                <span>KC trung bình mỗi người</span>
              </div>
              <div>
                <b>{fmtHours(result.totals.liveSeconds)}</b>
                <span>Giờ LIVE</span>
              </div>
              <div>
                <b>{fmtNum(result.totals.liveDays)}</b>
                <span>Ngày LIVE hợp lệ</span>
              </div>
              <div>
                <b>{fmtShort(result.totals.pkDiamonds)}</b>
                <span>KC từ PK</span>
              </div>
              <div>
                <b>{fmtNum(result.totals.under10k)}</b>
                <span>Dưới 10.000 KC</span>
              </div>
            </div>
            <div className={styles.share}>
              <small>Top 10 chiếm</small>
              <div className={styles.track}>
                <i style={{ width: `${Math.round(result.totals.top10Share * 100)}%` }} />
              </div>
              <small>{fmtPct(result.totals.top10Share, 0)} tổng KC</small>
              <Button size="small" ghost icon={<CopyOutlined />} onClick={copySummary}>
                Sao chép tổng
              </Button>
              <Button size="small" ghost icon={<DownloadOutlined />} loading={exporting} onClick={doExport}>
                Xuất Excel
              </Button>
            </div>
          </section>

          {(result.segments.length > 1 || result.daily.length > 1) && (
            <section className={styles.chartPanel}>
              <div className={styles.sideHead}>
                {result.segments.length > 1 ? 'KC theo tháng' : 'KC theo ngày'} · 5 người nhiều nhất + còn lại
              </div>
              <StackChart result={result} />
            </section>
          )}

          <section className={styles.main}>
            <div className={styles.panel}>
              <div className={styles.ptop}>
                <b>
                  {multi ? `Từng tháng · ${result.segments.length} tháng` : 'Chi tiết từng Creator'}
                  {rowFilter && (
                    <Tag closable onClose={() => setRowFilter(null)} className={styles.filterTag}>
                      {reviewCards.find((c) => c.filter === rowFilter)?.label}
                    </Tag>
                  )}
                </b>
                <Segmented
                  size="small"
                  value={quick}
                  onChange={(v) => setQuick(v as Quick)}
                  options={[
                    { value: 'all', label: 'Tất cả' },
                    { value: 'kc', label: 'Có KC' },
                    ...(result.compare?.available
                      ? [
                          { value: 'up', label: 'Tăng' },
                          { value: 'drop', label: 'Giảm > 30%' },
                        ]
                      : []),
                    ...(result.rows.some((r) => !r.fromList) && result.rows.some((r) => r.fromList)
                      ? [
                          { value: 'list', label: 'Từ danh sách' },
                          { value: 'email', label: 'Hệ thống thêm' },
                        ]
                      : []),
                  ]}
                />
              </div>
              {rows.length ? (
                <ResultTable result={result} rows={rows} groupBy={groupBy} />
              ) : (
                <Empty className={styles.empty} description="Không có Creator nào khớp bộ lọc." />
              )}
            </div>

            <aside className={styles.side}>
              <div className={styles.sideHead}>Cần xem lại</div>
              {reviewCards.map((c) => {
                const card = (
                  <button
                    type="button"
                    key={c.key}
                    className={`${styles.rv} ${styles[`rv_${c.tone}`]} ${c.filter && rowFilter === c.filter ? styles.rvOn : ''}`}
                    disabled={!c.count}
                    onClick={() => c.filter && setRowFilter(rowFilter === c.filter ? null : c.filter)}
                  >
                    <b>{c.label}</b>
                    <span className={styles.rvNum}>{fmtNum(c.count)}</span>
                    <small>{c.hint}</small>
                  </button>
                );
                return c.list && c.count ? (
                  <Popover
                    key={c.key}
                    trigger="click"
                    placement="left"
                    title={c.label}
                    content={
                      <div className={styles.rvList}>
                        {c.list.slice(0, 200).map((x, i) => (
                          <div key={i}>{x}</div>
                        ))}
                        {c.extra}
                      </div>
                    }
                  >
                    {card}
                  </Popover>
                ) : (
                  card
                );
              })}
            </aside>
          </section>
        </Spin>
      )}

      <Modal
        open={!!extract}
        title={`Chọn cột ID trong ${extract?.name || ''}`}
        okText="Thêm vào danh sách"
        cancelText="Huỷ"
        onOk={applyExtract}
        onCancel={() => setExtract(null)}
      >
        <p className={styles.hint}>Đã đoán cột giống ID nhất; chọn cột khác nếu sai.</p>
        <Radio.Group
          value={extract?.pick}
          onChange={(e) => extract && setExtract({ ...extract, pick: e.target.value })}
          className={styles.colPick}
        >
          {extract?.columns.map((c) => (
            <Radio key={c.index} value={c.index}>
              <b>{c.header}</b> · {c.values.length} ô
              {c.uidLike + c.nameLike > 0 && ` · ${c.uidLike} UID, ${c.nameLike} @ID`}
              <div className={styles.colSample}>{c.values.slice(0, 4).join(', ')}</div>
            </Radio>
          ))}
        </Radio.Group>
      </Modal>
    </div>
  );
};

export default KcLookupPage;
